<template>
    <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div class=" bg-white shadow-xl  overflow-hidden">
            <!-- Header avec image de couverture -->
            <div class="h-72 bg-gradient-to-br from-[#ff7e41]  to-[#2A3992] relative">
                <!-- Motif décoratif -->
                <div class="absolute inset-0 opacity-10">
                    <div class="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
                    <div class="absolute top-20 right-20 w-20 h-20 border border-white rounded-full"></div>
                    <div class="absolute bottom-10 left-1/3 w-16 h-16 border border-white rounded-full"></div>
                </div>

                <!-- Photo de profil -->
                <div class="absolute left-8 -bottom-16 z-1">
                    <div class="relative">
                        <div class="w-32 h-32 rounded-full border-6 border-white overflow-hidden shadow-2xl bg-white">
                            <img
                                :src="author.profil"
                                alt="Profile"
                                class="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <!-- Section profil -->
                <div class="lg:col-span-1 pt-20 px-8 pb-8 border-r border-gray-100">
                    <div class="max-w-sm">
                        <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ author.name }}</h1>
                        <p class="text-[#ff7e41] font-semibold text-lg mb-4">{{ getAuthorType(author.author_type) }}</p>

                        <!-- Stats améliorées -->
                        <div class="bg-gray-50 rounded-xl p-6 mb-6">
                            <div class="grid grid-cols-2 gap-6">
                                <div class="text-center">
                                    <div class="text-3xl font-bold text-gray-900 mb-1">{{ author.books?.length || 0 }}</div>
                                    <div class="text-sm text-gray-600 font-medium">Ouvrages</div>
                                </div>
                                <div class="text-center">
                                    <div class="text-3xl font-bold text-gray-900 mb-1">{{ uniqueGenres.length }}</div>
                                    <div class="text-sm text-gray-600 font-medium">Type</div>
                                </div>
                            </div>
                            <div class="border-t border-gray-200 mt-4 pt-4">
                                <div class="text-center">
                                    <div class="text-2xl font-bold text-[#ff7e41] mb-1">{{ totalPages }}</div>
                                    <div class="text-sm text-gray-600 font-medium">Pages totales</div>
                                </div>
                            </div>
                        </div>
                        <!-- Genres favoris -->
                        <div class="mb-6">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3" v-if="displayBooks.length">Genres de prédilection</h3>
                            <div class="flex flex-wrap gap-2">
                            <span v-for="book in uniqueGenres" :key="book"
                                  class="px-3 py-1 bg-gradient-to-r from-[#ff7e41] to-[#ff6b35] text-white text-xs font-semibold rounded-full">
                                {{ book }}
                            </span>
                            </div>
                        </div>
                        <!-- Bio -->
                        <div class="mb-6" v-if="author.bio">
                            <h3 class="text-lg font-semibold text-gray-900 mb-3">À propos</h3>
                            <p class="text-gray-600 leading-relaxed text-sm">
                                {{ author.bio }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Contenu des livres -->
                <div class="lg:col-span-2 p-8 pt-12">
                    <!-- Header section -->
                    <div class="flex items-center justify-between mb-8 pb-3 border-b border-gray-100">
                        <div>
                            <h2 class="text-3xl font-bold text-gray-900 mb-2">Ouvrages</h2>
                            <p class="text-gray-600">Découvrez les ouvrages de cet auteur</p>
                        </div>
                    </div>

                    <!-- Grille des livres -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        <!-- Cartes de chargement -->
                        <div v-if="loading" v-for="n in 6" :key="n"
                             class="bg-white rounded-2xl overflow-hidden shadow-md">
                            <div class="relative">
                                <div class="w-full h-64 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-shimmer relative overflow-hidden">
                                    <div class="shimmer-effect"></div>
                                </div>
                            </div>
                            <div class="p-5">
                                <div class="h-5 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded mb-3 bg-shimmer relative overflow-hidden">
                                    <div class="shimmer-effect"></div>
                                </div>
                                <div class="h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded w-3/4 mb-2 bg-shimmer relative overflow-hidden">
                                    <div class="shimmer-effect"></div>
                                </div>
                                <div class="h-3 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded w-1/2 bg-shimmer relative overflow-hidden">
                                    <div class="shimmer-effect"></div>
                                </div>
                            </div>
                        </div>

                        <!-- Cartes des livres -->
                        <div
                            v-else
                            v-for="book in displayBooks"
                            :key="book.id"
                            @click="navigationTo(book)"
                            class="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500
                            hover:-translate-y-2 group cursor-pointer border border-gray-100 hover:border-[#ff7e41]/30"
                        >

                            <div class="relative overflow-hidden">
                                <img :src="book.banners" :alt="book.title"
                                     class="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700" />

                                <!-- Badge genre -->
                                <div class="absolute top-3 left-3">
                                  <span :class="book.type"
                                        class="px-3 py-1 text-white text-xs font-bold rounded-full shadow-lg">
                                    {{ book.book_type }}
                                  </span>
                                </div>

                                <!-- Note/Rating -->
                                <div v-if="book.rating" class="absolute top-3 right-3 bg-white/90 px-2 py-1 rounded-full">
                                    <div class="flex items-center space-x-1">
                                        <svg class="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                        </svg>
                                        <span class="text-xs font-bold text-gray-700">{{ book.rating }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="p-5">
                                <h3 class="font-bold text-gray-900 mb-2 line-clamp-2 text-lg group-hover:text-[#ff7e41] transition-colors">
                                    {{ book.title }}
                                </h3>
                                <p class="text-gray-600 text-sm mb-3 font-medium">{{ book.author }}</p>

                                <div class="flex items-center justify-between text-xs text-gray-500">
                                  <span class="flex items-center space-x-1">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                                    </svg>
                                    <span>{{ book.pages }} pages</span>
                                  </span>
                                    <span class="font-semibold text-[#ff7e41]">{{ book.book_type || '-' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Message si aucun livre -->
                    <div v-if="!loading && (!displayBooks || displayBooks.length === 0)"
                         class="text-center py-16">
                        <div class="text-gray-400 mb-4">
                            <svg class="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                            </svg>
                        </div>
                        <h3 class="text-xl font-semibold text-gray-600 mb-2">Aucun livre trouvé</h3>
                        <p class="text-gray-500">La collection sera bientôt enrichie de nouveaux ouvrages.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import {route} from "@/helper.js";

const props = defineProps({
    author: {
    }
})

const loading = ref(true)


const displayBooks = computed(() => {
    return props.author.books && props.author.books.length > 0
        ? props.author.books
        :[]
})

const uniqueGenres = [...new Set(displayBooks.value.map(book => book.book_type))]
const totalPages = computed(() => {
    return displayBooks.value.reduce((total, book) => total + !isNaN(parseInt(book.pages)) ? (parseInt(book.pages)) : 0, 0)
})
const getAuthorType = (type) => {
    if (type === 'external') {
        return 'Externe'
    }
    return 'Etudiant'
}
const navigationTo=(book)=>{
    window.location.href = route('show-book', { book: book.id,slug:book?.slug });
}

onMounted(() => {
    setTimeout(() => {
        loading.value = false
    }, 1000)
})
</script>

<style scoped>
@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

.bg-shimmer {
    background-size: 200% 100%;
}

.shimmer-effect {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    animation: shimmer 0.5s ease-in-out infinite;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Transitions fluides */
* {
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Scrollbar personnalisée */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
}

/* Amélioration de la sélection de texte */
::selection {
    background: #ff7e41;
    color: white;
}

/* Focus states */
button:focus, select:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 126, 65, 0.1);
}
</style>
